import { useState } from "react";
import clsx from "clsx";
import { AlertTriangle, Camera, Play, X } from "lucide-react";
import type { AirportConfig, Turn } from "@yb/core";
import { hhmm } from "../lib/format";
import { momentLabel, moments, staleMinutes, stateTone, stateWord, stepBars, tailCheck, tailProofText, type CameraMedia, type CameraTurn, type TailCheck } from "../lib/apron";

const ms = (s: string | null | undefined) => (s ? Date.parse(s) : null);

const TONE: Record<"ok" | "info" | "warn" | "bad" | "off", string> = {
  ok: "bg-ok/15 text-ok",
  info: "bg-info/15 text-info",
  warn: "bg-warn/15 text-warn",
  bad: "bg-bad/15 text-bad",
  off: "bg-ink-3/15 text-ink-3",
};

/**
 * The cameras' word for a turn, as a small chip: on a bar of the stand board, on a ramp
 * card, and at the top of the Cameras section. Solid enough to read on an airline-coloured bar.
 */
export function CameraChip({ turn, onBar = false, className }: { turn: CameraTurn; onBar?: boolean; className?: string }) {
  const tone = stateTone(turn.state);
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center gap-0.5 rounded px-1 text-[9.5px] leading-[14px] font-bold whitespace-nowrap",
        onBar ? { ok: "bg-ok text-white", info: "bg-info text-white", warn: "bg-warn text-white", off: "bg-ink-3 text-white" }[tone] : TONE[tone],
        className,
      )}
      title={`What the cameras see: ${stateWord(turn.state).toLowerCase()}`}
    >
      <Camera className="size-2.5" aria-hidden />
      {stateWord(turn.state)}
    </span>
  );
}

/** "Camera feed last heard 22 min ago", when a live turn has gone quiet. */
export function StaleNote({ turn, now }: { turn: CameraTurn; now: number }) {
  const min = staleMinutes(turn, now);
  if (min === null) return null;
  const text = min < 120 ? `${min} min` : `${Math.floor(min / 60)} h ${min % 60} min`;
  return <div className="mt-1.5 rounded-md bg-warn/10 px-2 py-1 text-[11.5px] font-semibold text-warn">Camera feed last heard {text} ago</div>;
}

/**
 * The Cameras section of the flight panel: what Vectro Apron saw of this turn, beside what
 * the plan and the schedule say. Read only; nothing here changes the plan.
 */
export function CamerasSection({ cam, turn, config, tz, plannedStandId, now }: { cam: CameraTurn; turn: Turn; config: AirportConfig; tz: string; plannedStandId: string | null; now: number }) {
  const [playing, setPlaying] = useState<CameraMedia | null>(null);
  const tail = tailCheck(cam, turn);
  const steps = stepBars(cam.steps, now);
  const shots = moments(cam.media).filter((m) => !(tail?.proof && m.moment === "registration" && !m.clip));
  // This plan follows its own rules, so a difference from the cameras is not news unless asked for.
  const otherStand = config.settings.apronShowPlanDifferences === true && cam.standId && plannedStandId && cam.standId !== plannedStandId;
  const plannedName = config.stands.find((s) => s.id === plannedStandId)?.name ?? plannedStandId;
  const risk = cam.prediction?.risk;

  return (
    <div className="rounded-lg border border-line p-2.5">
      <div className="flex items-center gap-1.5 text-[12px] font-semibold">
        <Camera className="size-4 text-info" /> Cameras
        <CameraChip turn={cam} className="ml-1" />
        {cam.stateSince && <span className="tabular text-[11px] font-normal text-ink-3">since {hhmm(ms(cam.stateSince), tz)}</span>}
      </div>
      <StaleNote turn={cam} now={now} />

      {cam.conflict && (
        <div className="mt-1.5 flex gap-1.5 rounded-md bg-bad/10 px-2 py-1.5 text-[12px] text-bad">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{cam.conflict.summary}</span>
        </div>
      )}

      <div className="tabular mt-2 grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="text-ink-3">On block, cameras</div>
          <div className="font-semibold">{hhmm(ms(cam.onBlockAt), tz)}</div>
          {turn.arr && (
            <div className="text-ink-3">
              sched {hhmm(turn.arr.scheduled, tz)}
              {turn.arr.actual != null && ` · actual ${hhmm(turn.arr.actual, tz)}`}
            </div>
          )}
        </div>
        <div>
          <div className="text-ink-3">Off block, cameras</div>
          <div className="font-semibold">{hhmm(ms(cam.offBlockAt), tz)}</div>
          {turn.dep && (
            <div className="text-ink-3">
              sched {hhmm(turn.dep.scheduled, tz)}
              {turn.dep.actual != null && ` · actual ${hhmm(turn.dep.actual, tz)}`}
            </div>
          )}
        </div>
      </div>

      {tail && <TailProof check={tail} cam={cam} turn={turn} tz={tz} />}
      {otherStand && (
        <div className="mt-1.5 text-[11.5px] text-warn">
          Cameras see it on {cam.standName}; the plan has {plannedName}.
        </div>
      )}
      {!cam.standId && cam.stand && <div className="mt-1.5 text-[11px] text-ink-3">Cameras report gate {cam.stand}, which is not a stand set up here.</div>}
      {cam.prediction?.expectedPushbackAt && (
        <div className="mt-1.5 text-[11px] text-ink-2">
          Pushback expected {hhmm(ms(cam.prediction.expectedPushbackAt), tz)}
          {risk && risk !== "on_track" && <span className={clsx("ml-1 font-semibold", risk === "critical" ? "text-bad" : "text-warn")}>· {risk === "critical" ? "at risk" : "may be late"}</span>}
        </div>
      )}

      {steps && (
        <div className="mt-2.5">
          <div className="text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Steps seen</div>
          <div className="mt-1 space-y-1">
            {steps.bars.map((s) => (
              <div key={s.key} className="flex items-center gap-2 text-[11px]">
                <span className="w-28 shrink-0 truncate" title={s.label}>
                  {s.label}
                </span>
                <div className="relative h-2.5 flex-1 rounded-full bg-panel-2">
                  <div
                    className={clsx("absolute inset-y-0 rounded-full", s.open ? "bg-info/60" : "bg-info")}
                    style={{ left: `${s.left}%`, width: `${s.width}%` }}
                    title={`${hhmm(s.start, tz)}–${s.open ? "still going" : hhmm(s.end, tz)}`}
                  />
                </div>
                <span className="tabular w-[74px] shrink-0 text-right text-ink-3">
                  {hhmm(s.start, tz)}–{s.open ? "now" : hhmm(s.end, tz)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {shots.length > 0 && (
        <div className="mt-2.5 grid grid-cols-2 gap-2">
          {shots.map((m) => {
            const play = m.clip;
            const when = m.still?.capturedAt ?? m.clip?.capturedAt ?? null;
            return (
              <figure key={m.moment} className="min-w-0">
                <button
                  className={clsx("relative block aspect-video w-full overflow-hidden rounded-md border border-line bg-panel-2", play ? "cursor-pointer hover:border-ink-3" : "cursor-default")}
                  onClick={() => play && setPlaying(play)}
                  disabled={!play}
                  title={play ? "Play the clip" : m.still?.caption ?? undefined}
                >
                  {m.still ? (
                    <img src={m.still.url} alt={m.still.caption ?? momentLabel(m.moment)} loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <span className="grid h-full place-items-center text-[11px] text-ink-3">No still</span>
                  )}
                  {play && (
                    <span className="absolute inset-0 grid place-items-center">
                      <span className="rounded-full bg-black/55 p-1.5 text-white">
                        <Play className="size-3.5" />
                      </span>
                    </span>
                  )}
                </button>
                <figcaption className="mt-0.5 text-[10.5px] leading-tight">
                  <span className="font-semibold">{momentLabel(m.moment)}</span>
                  {when && <span className="tabular text-ink-3"> · {hhmm(ms(when), tz)}</span>}
                  {m.still?.caption && <div className="truncate text-ink-3" title={m.still.caption}>{m.still.caption}</div>}
                </figcaption>
              </figure>
            );
          })}
        </div>
      )}

      <div className="mt-2 text-[10.5px] text-ink-3">From Vectro Apron · revision {cam.revision} at {hhmm(ms(cam.at), tz)}. Shown for reference; the plan does not change from it.</div>

      {playing && <ClipPlayer clip={playing} title={`${momentLabel(playing.moment)} · ${hhmm(ms(playing.capturedAt), tz)}`} onClose={() => setPlaying(null)} />}
    </div>
  );
}

/** A clip, large, over everything; closed by the button, the backdrop or Escape. */
/**
 * The tail check and its proof: the AODB's tail beside what the cameras read, the verdict,
 * and the tail crop that confirmed the read, with when, which camera and its fingerprint, so
 * a "verified" or a "wrong tail" can be shown to anyone who asks.
 */
function TailProof({ check, cam, turn, tz }: { check: TailCheck; cam: CameraTurn; turn: Turn; tz: string }) {
  const [copied, setCopied] = useState(false);
  const p = check.proof;
  return (
    <div className={clsx("mt-2 rounded-md border p-2", check.verdict === "mismatch" ? "border-bad" : "border-line")}>
      <div className={clsx("rounded px-2 py-1 text-[11.5px] font-semibold", TONE[check.tone])}>{check.text}</div>
      <div className="tabular mt-1.5 grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="text-ink-3">AODB</div>
          <div className="font-semibold">{check.aodb ?? "No tail"}</div>
        </div>
        <div>
          <div className="text-ink-3">Cameras read</div>
          <div className="font-semibold">
            {check.camera ?? "Not yet"}
            {check.confidence != null && <span className="font-normal text-ink-3"> · {Math.round(check.confidence * 100)}% sure</span>}
          </div>
        </div>
      </div>
      {check.apronSchedule && <div className="mt-1 text-[11px] text-warn">{`Apron compared it against ${check.apronSchedule}, not the AODB's tail.`}</div>}
      {p ? (
        <figure className="mt-2">
          <a href={p.url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-md border border-line bg-panel-2" title="Open the full image">
            <img src={p.url} alt={`Tail crop: ${check.camera ?? "registration"}`} loading="lazy" className="max-h-40 w-full object-contain" />
          </a>
          <figcaption className="tabular mt-0.5 text-[10.5px] leading-snug text-ink-3">
            {`Tail crop${p.capturedAt ? ` · ${hhmm(ms(p.capturedAt), tz)}` : ""}${p.camera ? ` · camera ${p.camera}` : ""} · SHA-256 ${p.sha256.slice(0, 12)}…`}
          </figcaption>
        </figure>
      ) : (
        check.camera && <div className="mt-1.5 text-[11px] text-warn">No tail crop came with this read, so there is nothing to show as proof.</div>
      )}
      <button
        className="mt-1.5 rounded border border-line px-2 py-0.5 text-[11px] font-semibold hover:bg-panel-2"
        onClick={() => {
          void navigator.clipboard?.writeText(tailProofText(check, cam, turn, tz)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        {copied ? "Copied" : "Copy proof"}
      </button>
    </div>
  );
}

function ClipPlayer({ clip, title, onClose }: { clip: CameraMedia; title: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      role="dialog"
      aria-label={title}
    >
      <div className="w-full max-w-3xl rounded-xl border border-line bg-panel shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center border-b border-line px-4 py-2.5">
          <div className="flex-1 text-[14px] font-bold">{title}</div>
          <button onClick={onClose} className="rounded p-1 text-ink-3 hover:bg-panel-2" aria-label="Close" autoFocus>
            <X className="size-4" />
          </button>
        </div>
        <video src={clip.url} controls autoPlay muted playsInline className="block max-h-[70vh] w-full bg-black" />
        {clip.caption && <div className="px-4 py-2 text-[12px] text-ink-2">{clip.caption}</div>}
      </div>
    </div>
  );
}

/** For a stand's label on the board: the cameras see something there the plan has not put there. */
export function StandCameraChip({ cam, tz }: { cam: CameraTurn; tz: string }) {
  const flights = [cam.arrival?.flightNumber, cam.departure?.flightNumber].filter(Boolean).join(" / ") || "an aircraft";
  return (
    <span title={`Cameras: ${stateWord(cam.state)} — ${flights}${cam.stateSince ? ` since ${hhmm(ms(cam.stateSince), tz)}` : ""}, not where the plan has it`}>
      <CameraChip turn={cam} />
    </span>
  );
}
